import { useState, useRef, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  IconButton, Tooltip, Popover, Typography, Button,
} from '@material-ui/core';
import MapIcon from '@material-ui/icons/Map';
import StreetviewIcon from '@material-ui/icons/Streetview';
import MyLocationIcon from '@material-ui/icons/MyLocation';
import { getJobsiteCoords } from '../hooks/useJobsite';
import PublicIcon from '@material-ui/icons/Public';

const useStyles = makeStyles(() => ({
  fabStack: {
    position: 'absolute',
    top: 58,
    right: 12,
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    zIndex: 1100,
  },
  fab: {
    width: 38,
    height: 38,
    background: '#ffffff',
    borderRadius: 8,
    boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
    '&:hover': { background: '#f5f5f5' },
  },
  popover: {
    padding: 12,
    minWidth: 180,
  },
  optionBtn: {
    textTransform: 'none',
    justifyContent: 'flex-start',
    fontSize: 13,
    padding: '6px 12px',
    width: '100%',
  },
  pickingBanner: {
    position: 'absolute',
    top: 58,
    left: '50%',
    transform: 'translateX(-50%)',
    background: 'rgba(21, 101, 192, 0.95)',
    color: '#fff',
    padding: '8px 20px',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 500,
    zIndex: 1200,
    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  cancelBtn: {
    color: '#fff',
    borderColor: 'rgba(255,255,255,0.5)',
    textTransform: 'none',
    fontSize: 12,
    padding: '2px 10px',
  },
  mcIcon: {
    width: 20,
    height: 20,
    borderRadius: 3,
    background: '#B45309',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 11,
    fontWeight: 700,
  },
}));

/**
 * Format a jobsite address for the MC Assessor ipa.aspx URL.
 * Mirrors the server-side convertStreetShit + formatAddressForCounty logic
 * (minus the async unit-resolution which requires DB access).
 */
function formatAddressForCounty(jobsite) {
  if (!jobsite?.address) return '';

  const streetAbbreviations = {
    ROAD: 'RD', STREET: 'ST', AVENUE: 'AVE', BOULEVARD: 'BLVD',
    DRIVE: 'DR', LANE: 'LN', COURT: 'CT', PLACE: 'PL',
    TERRACE: 'TER', PARKWAY: 'PKWY', CIRCLE: 'CIR', TRAIL: 'TRL',
    DRV: 'DR', STRT: 'ST',
  };
  const directionAbbreviations = {
    NORTH: 'N', SOUTH: 'S', EAST: 'E', WEST: 'W',
    NORTHEAST: 'NE', NORTHWEST: 'NW', SOUTHEAST: 'SE', SOUTHWEST: 'SW',
  };

  let address = jobsite.address.replace(/\./g, '').replace(/,/g, '').toUpperCase().trim();
  if (address.includes('SOLD-')) address = address.split('SOLD-')[1].trim();
  if (address.includes('SOLD HOME')) address = address.split('SOLD HOME')[1].trim();

  const parts = address.split(/\s+/).filter(Boolean);

  // Abbreviate direction (2nd word, after street number)
  if (parts[1] && directionAbbreviations[parts[1]]) {
    parts[1] = directionAbbreviations[parts[1]];
  }

  // Detect building/unit number
  let hasBldgNum = false;
  parts.forEach((item, i) => {
    if (item.includes('#')) {
      if (i > 0) parts[i - 1] = `${parts[i - 1]},`;
      hasBldgNum = true;
    }
  });

  // Abbreviate street suffix
  const suffixIdx = parts.length - (hasBldgNum ? 2 : 1);
  if (suffixIdx >= 0 && streetAbbreviations[parts[suffixIdx]]) {
    parts[suffixIdx] = streetAbbreviations[parts[suffixIdx]];
  }

  let formatted = parts.join(' ');

  // Append city/zip
  const city = (jobsite.city || '').toUpperCase().trim();
  const zip = (jobsite.zip || '').trim();
  if (city && zip) {
    formatted = `${formatted} ${city} ${zip}`;
  } else if (city) {
    formatted = `${formatted} ${city}`;
  }

  return formatted;
}

const MapQuickLinks = ({ jobsite, mapRef }) => {
  const classes = useStyles();
  const googleBtnRef = useRef(null);
  const [googlePopoverOpen, setGooglePopoverOpen] = useState(false);
  const [pickingStreetView, setPickingStreetView] = useState(false);
  const clickHandlerRef = useRef(null);

  const coords = getJobsiteCoords(jobsite);

  // Open Google Maps at coords
  const openGoogleMapView = useCallback(() => {
    setGooglePopoverOpen(false);
    if (!coords) return;
    const [lat, lng] = coords;
    window.open(`https://www.google.com/maps/@${lat},${lng},19z`, '_blank');
  }, [coords]);

  const openGoogleEarthView = useCallback(() => {
    setGooglePopoverOpen(false);
    if (!coords) return;
    const [lat, lng] = coords;
    window.open(`https://earth.google.com/web/@${lat},${lng},500a,5000d,01.3315h,78t,0r`, '_blank');
  }, [coords]);

  // Street View: let user click the map, then find nearest street view
  const startStreetViewPick = useCallback(() => {
    setGooglePopoverOpen(false);
    if (!mapRef?.current) return;
    setPickingStreetView(true);
    const map = mapRef.current;
    map.getContainer().style.cursor = 'crosshair';

    const handler = (e) => {
      map.getContainer().style.cursor = '';
      setPickingStreetView(false);
      map.off('click', handler);
      clickHandlerRef.current = null;

      const { lat, lng } = e.latlng;
      // Open Google Street View at clicked location
      // Google will find the nearest available street view
      window.open(
        `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat},${lng}`,
        '_blank'
      );
    };

    clickHandlerRef.current = handler;
    map.on('click', handler);
  }, [mapRef]);

  const cancelStreetViewPick = useCallback(() => {
    setPickingStreetView(false);
    if (mapRef?.current) {
      mapRef.current.getContainer().style.cursor = '';
      if (clickHandlerRef.current) {
        mapRef.current.off('click', clickHandlerRef.current);
        clickHandlerRef.current = null;
      }
    }
  }, [mapRef]);

  // Recenter the Leaflet map on the jobsite
  const zoomToJobsite = useCallback(() => {
    if (!coords || !mapRef?.current) return;
    mapRef.current.setView(coords, 18, { animate: true });
  }, [coords, mapRef]);

  // MC Assessor link — ipa.aspx with lat/lng + formatted address
  const openMCAssessor = useCallback(() => {
    if (!coords) return;
    const [lat, lng] = coords;
    const address = formatAddressForCounty(jobsite);
    const encodedAddress = encodeURIComponent(address);
    window.open(
      `https://maps.mcassessor.maricopa.gov/ipa.aspx?1=${lat}&2=${lng}&a=${encodedAddress}`,
      '_blank'
    );
  }, [coords, jobsite]);

  if (!jobsite?._id) return null;

  return (
    <>
      <div className={classes.fabStack}>
        {/* Zoom-to-jobsite button — quick way back to the property */}
        <Tooltip title="Zoom to jobsite" placement="left">
          <IconButton
            className={classes.fab}
            onClick={zoomToJobsite}
            size="small"
          >
            <MyLocationIcon style={{ fontSize: 20, color: '#1976d2' }} />
          </IconButton>
        </Tooltip>

        {/* Google Maps button */}
        <Tooltip title="Google Maps" placement="left">
          <IconButton
            ref={googleBtnRef}
            className={classes.fab}
            onClick={() => setGooglePopoverOpen(true)}
            size="small"
          >
            <MapIcon style={{ fontSize: 20, color: '#4285F4' }} />
          </IconButton>
        </Tooltip>

        {/* MC Assessor button */}
        <Tooltip title="Maricopa County Assessor" placement="left">
          <IconButton
            className={classes.fab}
            onClick={openMCAssessor}
            size="small"
          >
            <div className={classes.mcIcon}>MC</div>
          </IconButton>
        </Tooltip>
      </div>

      {/* Google Maps popover — Map View vs Street View */}
      <Popover
        open={googlePopoverOpen}
        anchorEl={googleBtnRef.current}
        onClose={() => setGooglePopoverOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        classes={{ paper: classes.popover }}
      >
        <Typography style={{ fontSize: 12, fontWeight: 600, color: '#888', marginBottom: 6 }}>
          Open in Google Maps
        </Typography>
        <Button
          className={classes.optionBtn}
          startIcon={<MapIcon style={{ fontSize: 16 }} />}
          onClick={openGoogleMapView}
        >
          Map View
        </Button>
        <Button
          className={classes.optionBtn}
          startIcon={<PublicIcon style={{ fontSize: 16 }} />}
          onClick={openGoogleEarthView}
        >
          Google Earth
        </Button>
        <Button
          className={classes.optionBtn}
          startIcon={<StreetviewIcon style={{ fontSize: 16 }} />}
          onClick={startStreetViewPick}
        >
          Street View (click map)
        </Button>
      </Popover>

      {/* Street View pick mode banner */}
      {pickingStreetView && (
        <div className={classes.pickingBanner}>
          <StreetviewIcon style={{ fontSize: 18 }} />
          Click anywhere on the map for Street View
          <Button
            variant="outlined"
            size="small"
            className={classes.cancelBtn}
            onClick={cancelStreetViewPick}
          >
            Cancel
          </Button>
        </div>
      )}
    </>
  );
};

export default MapQuickLinks;
